import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Search, Plus, Trash2, Lock } from "lucide-react";
import useInventoryOwner from "../../hooks/useInventoryOwner";
import useBrandTranslation from "../../hooks/useBrandTranslation";
import useInventorySettings from "../../hooks/useInventorySettings";
import {
  fetchSupplies,
  fetchTreatments,
  createTreatment,
  updateTreatment,
} from "../../apis/api.inventory";
import {
  queryInventorySuppliesKey,
  queryInventoryTreatmentsKey,
} from "../../apis/queryKeys";
import { formatMoney, supplyCostDefault, supplyCostAlt } from "../../utils/inventoryFormat";
import customToast from "../common/Toaster";
import Button from "../common/Button";
import { Input } from "../common/Input";
import { Label } from "../common/Label";
import Spinner from "../common/Spinner";
import InventoryOwnerSelector from "./InventoryOwnerSelector";
import { Pagination } from "../common/Pagination";

const PAGE_SIZE = 7;

export default function TreatmentForm() {
  const { t } = useBrandTranslation();
  const { ownerId } = useInventoryOwner();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const isEditing = !!id;

  const { settings, decimalSeparator, defaultCurrencySymbol, altCurrency, altCurrencySymbol } =
    useInventorySettings(ownerId);
  const exchangeRate = settings?.exchange_rate ?? null;
  // Columna/total en moneda alternativa solo si el owner la configuró.
  const showAlt = !!altCurrency;

  const { data: supplies, isLoading: loadingSupplies } = useQuery({
    queryKey: queryInventorySuppliesKey(ownerId, ""),
    queryFn: () => fetchSupplies(ownerId, ""),
    enabled: !!ownerId,
  });

  const { data: treatments } = useQuery({
    queryKey: queryInventoryTreatmentsKey(ownerId),
    queryFn: () => fetchTreatments(ownerId),
    enabled: !!ownerId && isEditing,
  });
  const editingTreatment = useMemo(
    () => (treatments || []).find((tr) => tr.id === id) || null,
    [treatments, id]
  );

  const [name, setName] = useState("");
  // selected: { [supply_id]: quantity }
  const [selected, setSelected] = useState({});
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (!editingTreatment) return;
    setName(editingTreatment.name || "");
    const sel = {};
    (editingTreatment.items || []).forEach((it) => {
      sel[it.supply_id] = Number(it.quantity) || 0;
    });
    setSelected(sel);
  }, [editingTreatment]);

  const supplyById = useMemo(
    () => new Map((supplies || []).map((s) => [s.id, s])),
    [supplies]
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (supplies || []).filter((s) => !q || s.description.toLowerCase().includes(q));
  }, [supplies, search]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  // Totales en ambas monedas, convirtiendo con el tipo de cambio cuando el insumo
  // tiene el costo cargado en una sola. totalAlt = null si no se puede calcular.
  const { totalCost, totalAlt } = useMemo(() => {
    let def = 0;
    let alt = 0;
    let altComplete = true;
    Object.entries(selected).forEach(([supplyId, qty]) => {
      const supply = supplyById.get(supplyId);
      if (!supply) return;
      const q = Number(qty || 0);
      def += q * supplyCostDefault(supply, exchangeRate);
      const a = supplyCostAlt(supply, exchangeRate);
      if (a == null) altComplete = false;
      else alt += q * a;
    });
    return { totalCost: def, totalAlt: altComplete ? alt : null };
  }, [selected, supplyById, exchangeRate]);

  const addSupply = (supply) => {
    setSelected((prev) => (prev[supply.id] != null ? prev : { ...prev, [supply.id]: 1 }));
  };
  const setQty = (supplyId, value) => {
    const qty = value === "" ? "" : Number(value);
    setSelected((prev) => ({ ...prev, [supplyId]: Number.isNaN(qty) ? 0 : qty }));
  };
  const removeSupply = (supplyId) => {
    setSelected((prev) => {
      const next = { ...prev };
      delete next[supplyId];
      return next;
    });
  };

  const saveMutation = useMutation({
    mutationFn: (payload) =>
      isEditing ? updateTreatment(id, ownerId, payload) : createTreatment(ownerId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["inventory"] });
      customToast(
        isEditing
          ? t("inventory.treatment.updated", "Tratamiento actualizado")
          : t("inventory.treatment.created", "Tratamiento creado"),
        "success"
      );
      navigate("/inventory/treatments");
    },
    onError: (e) =>
      customToast(e?.message || t("inventory.treatment.saveError", "Error al guardar el tratamiento"), "danger"),
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!name.trim()) {
      customToast(t("inventory.treatment.requiredName", "Ingresá el nombre del tratamiento"), "warning");
      return;
    }
    const items = Object.entries(selected)
      .map(([supply_id, quantity]) => ({ supply_id, quantity: Number(quantity) || 0 }))
      .filter((it) => it.quantity > 0);
    if (items.length === 0) {
      customToast(t("inventory.treatment.requiredItems", "Agregá al menos un insumo"), "warning");
      return;
    }
    saveMutation.mutate({ name: name.trim(), items });
  };

  if (loadingSupplies) return <Spinner className="w-10 h-10 mx-auto my-10" />;

  const selectedEntries = Object.entries(selected);

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-3xl mx-auto">
        <Button variant="outline" onClick={() => navigate("/inventory/treatments")} className="mb-4">
          <ArrowLeft className="w-4 h-4 mr-2" />
          {t("inventory.back", "Volver")}
        </Button>

        <h1 className="text-2xl font-bold text-gray-900 mb-4">
          {isEditing
            ? t("inventory.treatment.editTitle", "Editar Tratamiento")
            : t("inventory.treatment.newTitle", "Nuevo Tratamiento")}
        </h1>

        <InventoryOwnerSelector readOnly className="mb-4" />

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="bg-white rounded-lg border border-gray-200 p-4">
            <Label className="text-sm font-semibold text-gray-800">
              {t("inventory.treatment.name", "Nombre")}
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t("inventory.treatment.namePlaceholder", "Peeling químico, APEX, BOTOX, COLELAP, etc.")}
            />
          </div>

          <div className="bg-white rounded-lg border border-gray-200 p-4 space-y-3">
            <div>
              <Label className="text-sm font-semibold text-gray-800">
                {t("inventory.treatment.supplies", "Insumos")}
              </Label>
              <p className="text-xs text-gray-500">
                {t("inventory.treatment.suppliesHint", "selecciona el insumo y cantidad que utiliza cada tratamiento")}
              </p>
            </div>

            {/* Insumos seleccionados (tabla superior con cantidades) */}
            {selectedEntries.length === 0 ? (
              <p className="text-xs text-gray-400 italic">
                {t("inventory.treatment.noSupplies", "Todavía no agregaste insumos")}
              </p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-gray-500 border-b">
                      <th className="py-1.5 pr-2">{t("inventory.treatment.supplies", "Insumo")}</th>
                      <th className="py-1.5 pr-2 w-24">{t("inventory.treatment.quantity", "Cantidad")}</th>
                      <th className="py-1.5 pr-2">
                        {t("inventory.treatment.subtotalIn", "Subtotal {{currency}}", {
                          currency: defaultCurrencySymbol || "",
                        })}
                      </th>
                      {showAlt && (
                        <th className="py-1.5 pr-2">
                          {t("inventory.treatment.subtotalIn", "Subtotal {{currency}}", {
                            currency: altCurrencySymbol || "",
                          })}
                        </th>
                      )}
                      <th className="py-1.5" />
                    </tr>
                  </thead>
                  <tbody>
                    {selectedEntries.map(([supplyId, qty]) => {
                      const s = supplyById.get(supplyId);
                      if (!s) return null;
                      const subtotal = Number(qty || 0) * supplyCostDefault(s, exchangeRate);
                      const altUnit = supplyCostAlt(s, exchangeRate);
                      const subtotalAlt = altUnit == null ? null : Number(qty || 0) * altUnit;
                      return (
                        <tr key={supplyId} className="border-b last:border-0">
                          <td className="py-1.5 pr-2 font-medium text-gray-800">{s.description}</td>
                          <td className="py-1.5 pr-2">
                            <Input
                              className="h-8"
                              inputMode="decimal"
                              value={qty}
                              onChange={(e) => setQty(supplyId, e.target.value)}
                            />
                          </td>
                          <td className="py-1.5 pr-2 text-gray-600">
                            {formatMoney(subtotal, defaultCurrencySymbol, decimalSeparator)}
                          </td>
                          {showAlt && (
                            <td className="py-1.5 pr-2 text-gray-600">
                              {subtotalAlt == null
                                ? "—"
                                : formatMoney(subtotalAlt, altCurrencySymbol, decimalSeparator)}
                            </td>
                          )}
                          <td className="py-1.5">
                            <button
                              type="button"
                              onClick={() => removeSupply(supplyId)}
                              className="inline-flex items-center gap-1 rounded-md border border-danger bg-danger-light px-2 py-1 text-xs text-danger transition-colors hover:bg-danger hover:text-white"
                              title={t("inventory.treatment.remove", "Eliminar")}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              {t("inventory.treatment.remove", "Eliminar")}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            {/* Buscador + listado paginado para agregar */}
            <div className="relative pt-2">
              <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <Input
                className="pl-9"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                placeholder={t("inventory.treatment.searchSupply", "Buscar insumo por nombre…")}
              />
            </div>

            <div className="divide-y border rounded-md">
              {pageItems.length === 0 ? (
                <p className="text-xs text-gray-400 p-3">
                  {t("inventory.list.empty", "Aún no tienes insumos cargados")}
                </p>
              ) : (
                pageItems.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-800 truncate">{s.description}</p>
                      <p className="text-xs text-gray-400">
                        {t("inventory.treatment.unitCost", "Costo unitario")}:{" "}
                        {formatMoney(supplyCostDefault(s, exchangeRate), defaultCurrencySymbol, decimalSeparator)}
                        {showAlt && supplyCostAlt(s, exchangeRate) != null && (
                          <>
                            {" · "}
                            {formatMoney(supplyCostAlt(s, exchangeRate), altCurrencySymbol, decimalSeparator)}
                          </>
                        )}
                      </p>
                    </div>
                    <button
                      type="button"
                      onClick={() => addSupply(s)}
                      disabled={selected[s.id] != null}
                      className="ml-2 inline-flex items-center gap-1 rounded-md border border-green-500 bg-green-500 px-2 py-1 text-xs text-white transition-colors hover:bg-green-600 hover:border-green-600 disabled:opacity-40"
                    >
                      <Plus className="w-3.5 h-3.5" />
                      {t("inventory.treatment.add", "Agregar")}
                    </button>
                  </div>
                ))
              )}
            </div>
            {totalPages > 1 && (
              <Pagination currentPage={page} totalPages={totalPages} onChange={setPage} />
            )}
          </div>

          {/* Costo total en vivo */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-gray-800 flex items-center gap-1">
                <Lock className="w-3.5 h-3.5 text-gray-400" />
                {t("inventory.treatment.totalCost", "Costo total del tratamiento")}
              </p>
              <p className="text-xs text-gray-400 italic">
                {t("inventory.treatment.totalCostPrivate", "ésta información es privada")}
              </p>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-green-600">
                {formatMoney(totalCost, defaultCurrencySymbol, decimalSeparator)}
              </p>
              {showAlt && (
                <p className="text-sm font-semibold text-gray-600">
                  {totalAlt == null
                    ? t("inventory.treatment.noExchangeRate", "Configurá el tipo de cambio en Monedas")
                    : formatMoney(totalAlt, altCurrencySymbol, decimalSeparator)}
                </p>
              )}
            </div>
          </div>

          {isEditing && editingTreatment?.updated_at && (
            <p className="text-xs text-gray-500">
              {t("inventory.treatment.lastUpdate", "Última actualización")}:{" "}
              {new Date(editingTreatment.updated_at).toLocaleString()}
            </p>
          )}

          <div className="flex justify-end">
            <Button type="submit" disabled={saveMutation.isPending}>
              {saveMutation.isPending
                ? t("inventory.common.saving", "Guardando…")
                : t("inventory.treatment.save", "Guardar Tratamiento")}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
